// frontend/src/components/Dashboard/ThemeArticles.js
import React, { useState, useEffect } from 'react';
import { getArticles } from '../../services/api';
import ArticleCard from './ArticleCard';
import LoadingSpinner from '../Common/LoadingSpinner';
import { motion } from 'framer-motion'; // Added Framer Motion

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1, // Time between each child animation
      delayChildren: 0.2, // Delay before starting the first child
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12,
    },
  },
};

const ThemeArticles = ({ articleIds, themeTitle }) => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!articleIds || articleIds.length === 0) {
      setArticles([]);
      return;
    }

    const fetchThemeArticles = async () => {
      setIsLoading(true);
      setError('');
      try {
        // Use the new 'ids' parameter for getArticles
        const response = await getArticles({ ids: articleIds.join(','), page_size: articleIds.length }); // Fetch all specified IDs
        setArticles(response.results || []);
        if ((response.results || []).length === 0) {
          console.warn(`[ThemeArticles] Theme "${themeTitle}": No articles found for IDs: ${articleIds.join(',')}`);
        }
      } catch (err) {
        console.error(`[ThemeArticles] Error fetching articles for theme "${themeTitle}" with IDs ${articleIds.join(',')}:`, err);
        setError(err.message || 'Failed to load articles for this theme.');
        setArticles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThemeArticles();
  }, [articleIds, themeTitle]); // Re-fetch if articleIds or themeTitle change

  if (isLoading) {
    return <div className="py-3"><LoadingSpinner /></div>;
  }

  if (error) {
    return <p className="text-xs text-red-500 italic mt-2">Error loading articles: {error}</p>;
  }

  if (articles.length === 0) {
    // This message is now more specific to whether articles were found for the *given IDs*
    return (
      <p className="text-xs text-gray-500 italic mt-2">
        Details for the highlighted articles in this theme could not be retrieved or were not found.
      </p>
    );
  }

  return (
    <motion.div 
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-2"
      variants={listVariants}
      initial="hidden"
      animate="visible" // Animate when component mounts or articles change and cause re-render
      key={articleIds.join('-')} // Add key to re-trigger animation if articleIds change
    >
      {articles.map(article => (
        // Each ArticleCard wrapper needs motion.div for itemVariants
        <motion.div key={article.id} variants={itemVariants}>
          <ArticleCard article={article} />
        </motion.div>
      ))}
    </motion.div>
  );
};

export default ThemeArticles;
